import React, { useState, useEffect } from "react";
import { getAuth, updateProfile } from "firebase/auth";
import { useToast } from "../../components/CustomToast";
import { getFirestore, doc, getDoc, updateDoc } from "firebase/firestore";
import Image from "../../images/user-avatar-80.png";
import { useNavigate } from "react-router-dom";

function AccountPanel() {
  const navigate = useNavigate();
  const [image, setImage] = useState(null);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const auth = getAuth();
  const db = getFirestore();
  const { addToast } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      if (auth.currentUser) {
        const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
        if (userDoc.exists()) {
          setFullName(userDoc.data().fullName || "");
        }
        setEmail(auth.currentUser.email || "");
      }
    };

    fetchUserData();
  }, [auth.currentUser, db]);

  const getInitialAvatar = () => {
    if (!fullName) return null;
    const initial = fullName.charAt(0).toUpperCase();
    return (
      <div className="w-full h-full bg-violet-600 flex items-center justify-center text-white text-2xl font-bold">
        {initial}
      </div>
    );
  };

  const handleFullNameUpdate = async () => {
    if (!fullName) return;
    setIsSaving(true);
    try {
      await updateProfile(auth.currentUser, { displayName: fullName });
      await updateDoc(doc(db, "users", auth.currentUser.uid), { fullName });
      addToast("Dit fulde navn er blevet opdateret!", "success");
    } catch (error) {
      console.error("Error updating display name:", error);
      addToast("Failed to update display name.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImage(URL.createObjectURL(file));
    }
  };

  const handleNavigate = () => {
    navigate("/reset-password");
  };

  return (
    <div className="grow">
      <div className="p-6 space-y-6">
        <h2 className="text-2xl text-gray-800 dark:text-gray-100 font-bold mb-5">
          Min konto
        </h2>

        {/* Picture */}
        <section>
          <div className="flex items-center justify-start w-full">
            <label
              htmlFor="dropzone-file"
              className="flex flex-col items-center justify-center h-20 w-20 border-2 border-gray-300 rounded-full cursor-pointer overflow-hidden"
              title="Upload Profile"
            >
              {image ? (
                <img
                  src={image}
                  alt="Uploaded Preview"
                  className="object-cover w-full h-full"
                />
              ) : fullName ? (
                getInitialAvatar()
              ) : (
                <img
                  src={Image}
                  alt="Default Avatar"
                  className="object-cover w-full h-full"
                />
              )}
            </label>
            <input
              id="dropzone-file"
              type="file"
              className="hidden"
              onChange={handleImageChange}
            />
          </div>
        </section>

        {/* Display Name */}
        <section>
          <h2 className="text-xl leading-snug text-gray-800 dark:text-gray-100 font-bold mb-1">
            Fulde navn
          </h2>
          <div className="mt-5">
            <label className="sr-only" htmlFor="display-name">
              Fulde navn
            </label>
            <input
              id="display-name"
              className="form-input w-full max-w-xl" // Updated to max-w-xl
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Ændre dit fulde navn"
            />
          </div>
        </section>

        {/* Email */}
        <section>
          <h2 className="text-xl leading-snug text-gray-800 dark:text-gray-100 font-bold mb-1">
            Email
          </h2>
          <div className="mt-5">
            <label className="sr-only" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              className="form-input w-full max-w-xl" // Updated to max-w-xl
              type="email"
              value={email}
              disabled
            />
          </div>
        </section>

        {/* Password */}
        <section>
          <h2 className="text-xl leading-snug text-gray-800 dark:text-gray-100 font-bold mb-1">
            Password
          </h2>
          <div className="text-sm">
            Har du glemt din adgangskode, eller vil du ændre den, så er det her.
          </div>
          <div className="mt-5">
            <button
              className="btn border-gray-200 dark:border-gray-700/60 shadow-sm text-violet-500"
              onClick={handleNavigate}
            >
              Ny adgangskode
            </button>
          </div>
        </section>
      </div>

      {/* Panel footer */}
      <footer>
        <div className="flex flex-col px-6 py-5 border-t border-gray-200 dark:border-gray-700/60">
          <div className="flex self-end">
            <button
              onClick={handleFullNameUpdate}
              className="btn bg-gray-900 text-gray-100 hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-800 dark:hover:bg-white ml-3"
              disabled={isSaving}
            >
              {isSaving ? (
                <div className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Gemmer...
                </div>
              ) : (
                "Gem ændringer"
              )}
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default AccountPanel;